import { useState } from "react";
import {
    Copy,
    Pen2,
    CheckCircle,
    FileDownload,
    Lock,
    MenuDots,
    TagHorizontal,
    Restart,
} from "@solar-icons/react/ssr";
import { useTheme } from "@mui/material";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import styled from "styled-components";

import { request } from "@/Api";
import useEntitlements from "@/auth/useEntitlements";
import BidCopyPopup from "@/bids/BidCopyPopup";
import BidTemplatePopup from "@/bids/BidTemplatePopup";
import { InternalLink } from "@/common/CommonComponents";
import { FEATURE_ACCOUNTING_STRATEGY } from "@/constants";
import {
    getClientCurrency,
    getReadOnly,
    getIsTemplate,
    store,
    optimisticToggleCleanBid,
} from "@/sheet/bidStore";
import { confirmedBidStatuses, BID_TYPE_CO } from "@/sheet/constants";
import InlineRename from "@/sheet/InlineRename";
import { downloadFile, getCurrencySymbol } from "@/utils";

const Container = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
`;
const TitleSpan = styled.span`
    font-size: 22px;
    white-space: nowrap;
`;

export default function BidTitle({ bid: paramBid, bidId, refreshBid }) {
    const theme = useTheme();

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const { hasFeature } = useEntitlements();
    const [showBidRename, setShowBidRename] = useState(false);
    const [bidTemplatePopupOpen, setBidTemplatePopupOpen] = useState(false);
    const [bidToCopy, setBidToCopy] = useState(null);

    const activeBid = paramBid || store.bid;
    const status = activeBid.status;
    const canActualize = confirmedBidStatuses.includes(activeBid.status);
    const bid = activeBid;
    const projectCurrency = paramBid?.project?.currency || getClientCurrency();
    const bidType = activeBid.bid_type;
    const readOnly = paramBid ? false : getReadOnly();
    const isTemplate = paramBid ? false : getIsTemplate();

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    const handleBidRename = (event) => {
        handleMenuClose(event);
        setShowBidRename(true);
    };

    const renameBid = (value) => {
        request.patch(`/bids/${bidId}`, { name: value }).then(() => {
            setShowBidRename(false);
        });
    };

    const closeRename = (event) => {
        event.stopPropagation();
        setShowBidRename(false);
    };

    const handleExcelExport = (event) => {
        handleMenuClose(event);
        downloadFile(`bids/${bidId}/excel-export`);
    };

    const handleCopy = (event) => {
        handleMenuClose(event);
        setBidToCopy(bid);
    };

    const handleTemplate = (event) => {
        handleMenuClose(event);
        setBidTemplatePopupOpen(true);
    };

    const handleRevertCleanBid = (event) => {
        handleMenuClose(event);
        optimisticToggleCleanBid(false);
        refreshBid(store.bid);
        request.put(`/bids/${bidId}/revert-clean`).catch(() => {
            optimisticToggleCleanBid(true);
            refreshBid(store.bid);
        });
    };

    return showBidRename ? (
        <InlineRename
            defaultName={bid.name}
            close={closeRename}
            rename={renameBid}
        />
    ) : (
        <div>
            {bid.project ? (
                <div style={{ paddingBottom: 10 }}>
                    <InternalLink
                        href={`/projects/${bid.project.id}`}
                        sx={{
                            textDecoration: "none",
                            fontSize: 14,
                            color: theme.palette.primary.main,
                        }}
                    >
                        {bid.project.name}
                    </InternalLink>
                    <Chip
                        label={
                            bidType == BID_TYPE_CO
                                ? bidType
                                : `${bidType} Bid Sheet`
                        }
                        color="warning"
                        size="small"
                        variant="outlined"
                        sx={{ marginLeft: 1 }}
                    />
                    {!readOnly && (
                        <Chip
                            icon={
                                <Lock
                                    size={12}
                                    style={{
                                        color: "#464646",
                                        paddingLeft: 3,
                                    }}
                                />
                            }
                            label={status}
                            size="small"
                            variant="outlined"
                            sx={{
                                background: "#F2F2F2",
                                color: "#464646",
                                marginLeft: 1,
                            }}
                        />
                    )}
                </div>
            ) : (
                <div>
                    <span style={{ fontSize: 14, color: "gray" }}>
                        Template
                    </span>
                </div>
            )}
            <Container>
                <TitleSpan>{bid.name}</TitleSpan>
                <IconButton sx={{ margin: "0 6px" }} onClick={handleMenuOpen}>
                    <MenuDots size={18} />
                </IconButton>
                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                >
                    {!readOnly && (
                        <MenuItem onClick={handleBidRename}>
                            <ListItemIcon>
                                <Pen2 size={18} />
                            </ListItemIcon>
                            <ListItemText>Rename</ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleExcelExport}>
                        <ListItemIcon>
                            <FileDownload size={18} />
                        </ListItemIcon>
                        <ListItemText>Export Excel</ListItemText>
                    </MenuItem>
                    {!isTemplate && (
                        <MenuItem onClick={handleCopy}>
                            <ListItemIcon>
                                <Copy size={18} />
                            </ListItemIcon>
                            <ListItemText>
                                Duplicate{" "}
                                {bid.is_change_order
                                    ? "Change Order"
                                    : "Bid Sheet"}
                            </ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleTemplate}>
                        <ListItemIcon>
                            <TagHorizontal size={18} />
                        </ListItemIcon>
                        <ListItemText>Save as Template</ListItemText>
                    </MenuItem>
                    {!readOnly && (
                        <MenuItem onClick={handleRevertCleanBid}>
                            <ListItemIcon>
                                <Restart size={18} />
                            </ListItemIcon>
                            <ListItemText>{`Unarchive ${getCurrencySymbol(projectCurrency)}0 lines`}</ListItemText>
                        </MenuItem>
                    )}

                    {hasFeature(FEATURE_ACCOUNTING_STRATEGY) &&
                        canActualize && (
                            <InternalLink
                                href={`/projects/${bid.project_id}/actuals`}
                                sx={{
                                    textDecoration: "none",
                                    color: "inherit",
                                }}
                            >
                                <MenuItem>
                                    <ListItemIcon>
                                        <CheckCircle size={18} />
                                    </ListItemIcon>
                                    <ListItemText>Actualize</ListItemText>
                                </MenuItem>
                            </InternalLink>
                        )}
                </Menu>
                <BidTemplatePopup
                    bidId={bidId}
                    bidTemplatePopupOpen={bidTemplatePopupOpen}
                    setBidTemplatePopupOpen={setBidTemplatePopupOpen}
                    isPublic={isTemplate && bid.bid_template.public}
                />
                <BidCopyPopup bid={bidToCopy} setBid={setBidToCopy} />
            </Container>
        </div>
    );
}
